import React from 'react';
import { Box, Typography, Tooltip, LinearProgress } from '@mui/material';
import { RegionCapacity } from '../../services/api';

type BarColor = 'success' | 'warning' | 'error';

interface Metric {
  key: string;
  label: string;
  short: string;
  used: number;
  limit: number | null | undefined;
}

const utilColor = (ratio: number): BarColor => {
  if (ratio >= 0.9) return 'error';
  if (ratio >= 0.7) return 'warning';
  return 'success';
};

const buildMetrics = (capacity: RegionCapacity): Metric[] => {
  const instanceLimit = capacity.effective_instance_limit ?? capacity.instance_limit;
  return [
    { key: 'inst', label: 'Instances', short: 'Inst', used: capacity.instance_used, limit: instanceLimit },
    { key: 'vcpu', label: 'vCPU', short: 'vCPU', used: capacity.vcpu_used, limit: capacity.vcpu_limit },
    { key: 'ip', label: 'Public IPs', short: 'IPs', used: capacity.ip_used, limit: capacity.ip_limit },
  ];
};

const ratioOf = (used: number, limit: number | null | undefined): number | null => {
  if (limit === null || limit === undefined || limit <= 0) return null;
  return Math.min(used / Math.max(limit, 1), 1);
};

const fmt = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : String(value);

interface CapacityBarsProps {
  capacity?: RegionCapacity | null;
}

/**
 * Compact, three-bar capacity readout for the main region row. Each bar shows
 * used/limit utilisation for instances, vCPU and public IPs and is colour-coded
 * by how close the region is to its limit.
 */
export const CapacityBars: React.FC<CapacityBarsProps> = ({ capacity }) => {
  if (!capacity) {
    return (
      <Typography variant="caption" color="text.secondary">
        Not synced
      </Typography>
    );
  }

  const metrics = buildMetrics(capacity);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, minWidth: 150 }}>
      {metrics.map((metric) => {
        const ratio = ratioOf(metric.used, metric.limit);
        const color: BarColor = ratio === null ? 'success' : utilColor(ratio);
        const valueLabel = `${fmt(metric.used)}/${fmt(metric.limit)}`;
        return (
          <Tooltip
            key={metric.key}
            title={`${metric.label}: ${valueLabel}${ratio !== null ? ` (${Math.round(ratio * 100)}%)` : ''}`}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '34px 1fr auto', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>
                {metric.short}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={ratio === null ? 0 : ratio * 100}
                color={color}
                sx={{ height: 5, borderRadius: 3, backgroundColor: 'action.hover' }}
              />
              <Typography
                variant="caption"
                sx={{ fontSize: '0.65rem', fontFamily: '"Space Mono", monospace', lineHeight: 1, whiteSpace: 'nowrap' }}
              >
                {valueLabel}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

interface RegionLimitCellProps {
  capacity?: RegionCapacity | null;
}

/**
 * Single-value limit readout for the main region row. Shows just the region's
 * bot limit — the manual override when one is set, otherwise the synced provider
 * quota — with no instance/vCPU/IP breakdown.
 */
export const RegionLimitCell: React.FC<RegionLimitCellProps> = ({ capacity }) => {
  const manualLimit = capacity?.manual_bot_limit;
  const syncedLimit = capacity?.effective_instance_limit ?? capacity?.instance_limit ?? null;
  const isManual = manualLimit !== null && manualLimit !== undefined;
  const limit = isManual ? manualLimit : syncedLimit;

  if (limit === null || limit === undefined) {
    return (
      <Typography variant="caption" color="text.secondary">
        Not synced
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 90 }}>
      <Typography variant="body2" sx={{ fontFamily: '"Space Mono", monospace', fontWeight: 600 }}>
        {limit}
      </Typography>
      <Typography variant="caption" color={isManual ? 'primary.main' : 'text.secondary'}>
        {isManual ? 'Manual' : 'Synced'}
      </Typography>
    </Box>
  );
};

interface CapacityDetailProps {
  capacity?: RegionCapacity | null;
}

/**
 * Larger, labelled capacity readout for the expanded region detail panel.
 */
export const CapacityDetail: React.FC<CapacityDetailProps> = ({ capacity }) => {
  if (!capacity) {
    return (
      <Typography variant="body2" color="text.secondary">
        Run “Sync Capacity” to load utilisation for this region.
      </Typography>
    );
  }

  const metrics = buildMetrics(capacity);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {metrics.map((metric) => {
        const ratio = ratioOf(metric.used, metric.limit);
        const color: BarColor = ratio === null ? 'success' : utilColor(ratio);
        return (
          <Box key={metric.key}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary">
                {metric.label}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: '"Space Mono", monospace' }}>
                {fmt(metric.used)} / {fmt(metric.limit)}
                {ratio !== null ? `  ·  ${Math.round(ratio * 100)}%` : ''}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={ratio === null ? 0 : ratio * 100}
              color={color}
              sx={{ height: 7, borderRadius: 4, backgroundColor: 'action.hover' }}
            />
          </Box>
        );
      })}
      {(capacity.vcpu_per_bot || capacity.last_synced_at) && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          {capacity.vcpu_per_bot ? `${capacity.vcpu_per_bot} vCPU/bot` : ''}
          {capacity.vcpu_per_bot && capacity.last_synced_at ? '  ·  ' : ''}
          {capacity.last_synced_at ? `synced ${new Date(capacity.last_synced_at).toLocaleString()}` : ''}
        </Typography>
      )}
    </Box>
  );
};
